import { keepPreviousData, useQuery } from "@tanstack/react-query";
import api from "./api";

// Sent messages (email/SMS) for a payment request (BFF: GET api/paymentrequests/{id}/messages,
// backed by messaging-service). Returns [{ id, paymentRequestId, communicationMethod, sentOn, hasArchive }].
async function fetchPaymentRequestMessages(paymentRequestId, { signal } = {}) {
  const { data } = await api.get(`api/paymentrequests/${paymentRequestId}/messages`, {
    signal,
    bypassInterceptor: true,
  });
  return data;
}

// Batch variant for a set of payment requests (BFF: POST api/paymentrequests/messages-lookup).
// Returns a flat array of the same message shape; callers group by paymentRequestId.
async function fetchMessagesLookup(paymentRequestIds, { signal } = {}) {
  const { data } = await api.post("api/paymentrequests/messages-lookup", paymentRequestIds, {
    signal,
    bypassInterceptor: true,
  });
  return data;
}

// Archive summaries for a set of message ids (BFF: POST api/messages/lookup).
// Returns [{ id, communicationMethod, sentOn, hasArchive }].
async function fetchMessagesByIds(messageIds, { signal } = {}) {
  const { data } = await api.post("api/messages/lookup", messageIds, {
    signal,
    bypassInterceptor: true,
  });
  return data;
}

// Archived content of a sent message (BFF: GET api/messages/{id}/content). 404 means no
// archived copy exists — bypassInterceptor stops the global 404 redirect so callers can
// render a fallback instead.
async function fetchMessageContent(messageId, { signal } = {}) {
  const { data } = await api.get(`api/messages/${messageId}/content`, {
    signal,
    bypassInterceptor: true,
  });
  return data; // { id, communicationMethod, subject, htmlBody, textBody }
}

function usePaymentRequestMessages(paymentRequestId, enabled = true) {
  return useQuery({
    queryKey: ["paymentRequestMessages", paymentRequestId],
    queryFn: ({ signal }) => fetchPaymentRequestMessages(paymentRequestId, { signal }),
    enabled: !!paymentRequestId && enabled,
    staleTime: 5 * 60_000,
  });
}

function useMessagesLookup(paymentRequestIds) {
  return useQuery({
    queryKey: ["messagesLookup", paymentRequestIds],
    queryFn: ({ signal }) => fetchMessagesLookup(paymentRequestIds, { signal }),
    enabled: paymentRequestIds?.length > 0,
    staleTime: 30_000,
    // The id set grows as the activity feed pages in, re-keying this query; keep the prior results on
    // screen while it refetches so the feed's "View message" buttons don't vanish and reappear (which
    // changes row heights above the viewport and makes the scroll jump).
    placeholderData: keepPreviousData,
  });
}

function useMessagesByIds(messageIds) {
  return useQuery({
    queryKey: ["messagesByIds", messageIds],
    queryFn: ({ signal }) => fetchMessagesByIds(messageIds, { signal }),
    enabled: messageIds?.length > 0,
    staleTime: 30_000,
    // Same as useMessagesLookup: hold the previous results across the re-key so document/email rows keep
    // their "View message" button instead of flickering it on every page load.
    placeholderData: keepPreviousData,
  });
}

// Raw bytes of an archived email attachment, opened in a new tab as a blob URL so the
// request stays authenticated (no raw URL navigation).
async function openMessageAttachment(messageId, index) {
  const { data } = await api.get(`api/messages/${messageId}/attachments/${index}`, {
    responseType: "blob",
    bypassInterceptor: true,
  });
  const url = URL.createObjectURL(data);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function useMessageContent(messageId, enabled = true) {
  return useQuery({
    queryKey: ["messageContent", messageId],
    queryFn: ({ signal }) => fetchMessageContent(messageId, { signal }),
    enabled: !!messageId && enabled,
    staleTime: 5 * 60_000,
  });
}

export {
  fetchPaymentRequestMessages,
  fetchMessagesLookup,
  fetchMessagesByIds,
  fetchMessageContent,
  openMessageAttachment,
  usePaymentRequestMessages,
  useMessagesLookup,
  useMessagesByIds,
  useMessageContent,
};
